import { useQuery } from '@tanstack/react-query';
import { useState, type ReactElement } from 'react';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { useDeleteBuild } from '@/features/builds/hooks/builds';
import { resolveBuildRunNumber } from '@/features/builds/utils/buildRunNumber';
import { PipelineRunEditDialog } from '@/features/pipelines/components/PipelineRunEditDialog';
import {
  buildPipelineRunColgroup,
  buildStageRunColgroup,
  nestedRunSkeletonColumns,
  nestedStageRunSkeletonColumns,
  PipelineNestedHeader,
  StageRunNestedHeader,
  type SortablePipelineRunField,
  type SortableStageRunField,
} from '@/features/pipelines/components/PipelineTableV2NestedLayout';
import {
  buildPipelineRunHref,
  resolvePipelineRunStageCount,
} from '@/features/pipelines/components/PipelineTableV2NestedRuns.helpers';
import { StageRunNestedRow } from '@/features/pipelines/components/PipelineTableV2StageRunNestedRow';
import { queryPipelineRunSummary, queryPipelineStageExecutions } from '@/features/pipelines/data';
import { useDeletePipelineExecution } from '@/features/pipelines/hooks/pipelines';
import {
  buildOperationPassRateCountsFromStatusCounts,
  buildOperationPassRateCountsFromStatusSummaries,
} from '@/features/shared/components/tableSystem/operationPassRateCounts';
import { TableActionsCell } from '@/features/shared/components/tableSystem/TableCells';
import { TableV2DurationCell } from '@/features/shared/components/tableSystem/TableV2DurationCell';
import { TableV2NestedEmptyState } from '@/features/shared/components/tableSystem/TableV2NestedEmptyState';
import { TableV2NestedFrame } from '@/features/shared/components/tableSystem/TableV2NestedFrame';
import {
  buildTableV2NestedBodyRowSx,
  queryAllTableV2NestedRows,
  tableV2NestedCarrierRowSx,
  tableV2NestedContentSx,
  tableV2NestedDepthSx,
} from '@/features/shared/components/tableSystem/TableV2NestedTable';
import {
  TableV2ChevronButton,
  TableV2DateTimeCell,
  TableV2NameCell,
  TableV2NestedCollapse,
  TableV2PassRateCell,
  TableV2StatusChip,
  TableV2TagGroup,
  tableV2ActionsColumnWidth,
  tableV2ChevronCellSx,
} from '@/features/shared/components/tableSystem/TableV2Primitives';
import { TableV2SkeletonRow } from '@/features/shared/components/tableSystem/TableV2Skeleton';
import { resolveTableV2NestedSkeletonRowCount } from '@/features/shared/components/tableSystem/TableV2SkeletonRows';
import { useNotifications } from '@/providers/notificationHooks';
import { getEntityActionLabel } from '@/shared/copy/actions';
import { getDialogMessage } from '@/shared/copy/dialogs';
import { getToastMessage } from '@/shared/copy/toasts';
import { useApiErrorMessage } from '@/shared/data/errors';
import { gridPaths, queryKeys } from '@/shared/hooks';
import { s } from '@/theme/tokens';
import { buildDateRangeFilters, buildEqFilter } from '@/ui/composites/grid/gridFilters';
import { Box, Table, TableBody, TableCell, TableHead, TableRow } from '@/ui/primitives';
import type { TagColorByName } from '@/features/shared/tags';
import type { ApiSort, GridQueryInput } from '@/ui/composites/grid/gridUtils';
import type {
  PipelineRunSummaryItemDto,
  PipelineStageExecutionListItemDto,
  QueryResult,
} from '@flowqa/shared';

const defaultPipelineRunSort: ApiSort = { field: 'startedAt', direction: 'desc' };
const defaultStageRunSort: ApiSort = { field: 'startedAt', direction: 'desc' };

const PipelineRunNestedRow = ({
  isLastVisibleRow,
  organisationId,
  row,
  projectId,
  tagColorByName,
}: {
  isLastVisibleRow: boolean;
  organisationId: string | null;
  row: PipelineRunSummaryItemDto;
  projectId: string;
  tagColorByName: TagColorByName;
}): ReactElement => {
  const [expanded, setExpanded] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const { notify } = useNotifications();
  const toErrorMessage = useApiErrorMessage();
  const deleteBuild = useDeleteBuild();
  const deletePipelineExecution = useDeletePipelineExecution();
  const stageCount = resolvePipelineRunStageCount(row);
  const rowName = row.buildKey ?? row.id;
  const runNumber = resolveBuildRunNumber(row.buildKey);
  const deletePending = deleteBuild.isPending || deletePipelineExecution.isPending;

  return (
    <>
      <TableRow
        sx={buildTableV2NestedBodyRowSx({
          depth: 1,
          hasRenderedDivider: !expanded && !isLastVisibleRow,
        })}
      >
        <TableCell align="center" sx={tableV2ChevronCellSx}>
          <TableV2ChevronButton
            expanded={expanded}
            label={expanded ? 'Collapse stage runs' : 'Expand stage runs'}
            onClick={() => {
              setExpanded((current) => !current);
            }}
          />
        </TableCell>
        <TableCell>
          <TableV2NameCell
            href={buildPipelineRunHref(row)}
            primary={runNumber === null ? (row.buildKey ?? row.id) : `#${String(runNumber)}`}
          />
        </TableCell>
        <TableCell>
          <TableV2StatusChip status={row.status} />
        </TableCell>
        <TableCell>
          <TableV2PassRateCell
            counts={
              row.stageStatusCounts
                ? buildOperationPassRateCountsFromStatusCounts(row.stageStatusCounts)
                : buildOperationPassRateCountsFromStatusSummaries(row.stageSummaries)
            }
          />
        </TableCell>
        <TableCell>
          <TableV2DurationCell value={row.durationMs} />
        </TableCell>
        <TableCell>
          <TableV2DateTimeCell value={row.timestamp} />
        </TableCell>
        <TableCell>
          <TableV2TagGroup tagColorByName={tagColorByName} tags={row.tags ?? []} />
        </TableCell>
        <TableCell align="center" sx={{ p: s('none'), width: tableV2ActionsColumnWidth }}>
          <TableActionsCell
            rowName={rowName}
            verticalAlign="center"
            editActionLabel={getEntityActionLabel('edit', 'Pipeline Run')}
            deleteActionLabel={getEntityActionLabel('delete', 'Pipeline Run')}
            rowTags={row.tags ?? []}
            showEditTagsAction={false}
            onEdit={() => {
              setEditOpen(true);
            }}
            onDelete={() => {
              setDeleteOpen(true);
            }}
            disableEdit={deletePending}
            disableDelete={deletePending}
          />
        </TableCell>
      </TableRow>
      <TableRow
        sx={[
          tableV2NestedCarrierRowSx,
          expanded && !isLastVisibleRow
            ? { '& > td': { borderBottom: (theme) => `1px solid ${theme.app.colors.border}` } }
            : {},
        ]}
      >
        <TableCell colSpan={8}>
          <PipelineRunExpandedStageRuns
            childCount={stageCount}
            expanded={expanded}
            organisationId={organisationId}
            pipelineExecutionId={row.pipelineExecutionId ?? row.id}
            projectId={projectId}
            tagColorByName={tagColorByName}
          />
        </TableCell>
      </TableRow>
      <PipelineRunEditDialog
        open={editOpen}
        projectId={projectId}
        row={row}
        rowName={rowName}
        onClose={() => {
          setEditOpen(false);
        }}
      />
      <ConfirmDialog
        open={deleteOpen}
        title={getDialogMessage('destructive.delete.title', { entity: 'pipelineRun' })}
        description={getDialogMessage('destructive.delete.simpleDescription', {
          entity: 'pipelineRun',
        })}
        confirmLabel={getDialogMessage('destructive.delete.confirmLabel', {
          entity: 'pipelineRun',
        })}
        confirmColor="red"
        loading={deletePending}
        onClose={() => {
          if (deletePending) return;
          setDeleteOpen(false);
        }}
        onConfirm={() => {
          const deleteMutation = row.pipelineExecutionId ? deletePipelineExecution : deleteBuild;
          deleteMutation.mutate(
            {
              id: row.pipelineExecutionId ?? row.id,
              projectId,
            },
            {
              onSuccess: () => {
                notify({
                  message: getToastMessage('entity.deleted', {
                    entity: 'pipeline run',
                    name: rowName,
                  }),
                  tone: 'green',
                });
                setDeleteOpen(false);
              },
              onError: (error) => {
                notify({ message: toErrorMessage(error), tone: 'red' });
              },
            },
          );
        }}
      />
    </>
  );
};

export const StageExpandedRuns = ({
  childCount,
  expanded,
  organisationId,
  pipelineId,
  projectId,
  stageRunsEndAt,
  stageRunsStartAt,
  stageName,
  tagColorByName,
}: {
  childCount: number;
  expanded: boolean;
  organisationId: string | null;
  pipelineId: string | null;
  projectId: string;
  stageRunsEndAt: string | null;
  stageRunsStartAt: string | null;
  stageName: string;
  tagColorByName: TagColorByName;
}): ReactElement => {
  const [sort, setSort] = useState<ApiSort>(defaultStageRunSort);
  const handleSortChange = (field: SortableStageRunField): void => {
    setSort((current) => ({
      field,
      direction: current.field === field && current.direction === 'desc' ? 'asc' : 'desc',
    }));
  };
  const query = useQuery({
    queryKey: queryKeys.dataGrid(
      'stage-runs',
      'v2-nested-stage-runs-by-stage',
      organisationId,
      projectId,
      pipelineId,
      stageName,
      stageRunsStartAt,
      stageRunsEndAt,
      sort.field,
      sort.direction,
    ),
    enabled: expanded,
    queryFn: async () =>
      await queryAllTableV2NestedRows(
        {
          page: 1,
          pageSize: 1,
          sorts: [sort],
          filters: [
            ...buildEqFilter('pipelineId', pipelineId),
            ...buildEqFilter('stageName', stageName),
            ...buildDateRangeFilters('startedAt', stageRunsStartAt, stageRunsEndAt),
          ],
        },
        async (
          nextInput: GridQueryInput,
        ): Promise<QueryResult<PipelineStageExecutionListItemDto>> =>
          await queryPipelineStageExecutions(projectId, organisationId, nextInput),
      ),
    staleTime: 30_000,
  });
  const header = (
    <StageRunNestedHeader nameHeaderLabel="Run" sort={sort} onSortChange={handleSortChange} />
  );
  const skeletonRowCount = resolveTableV2NestedSkeletonRowCount(childCount);

  return (
    <TableV2NestedCollapse expanded={expanded}>
      <Box sx={tableV2NestedContentSx}>
        {query.isLoading ? (
          <TableV2NestedFrame depth={1}>
            <Table aria-label="Stage runs loading" sx={tableV2NestedDepthSx[1].table}>
              {buildStageRunColgroup()}
              <TableHead>{header}</TableHead>
              <TableBody>
                {Array.from({ length: skeletonRowCount }, (_item, index) => (
                  <TableV2SkeletonRow
                    key={`stage-runs-by-stage-nested-skeleton-${String(index)}`}
                    columns={nestedStageRunSkeletonColumns}
                    isLastVisibleRow={index === skeletonRowCount - 1}
                    rowIndex={index}
                  />
                ))}
              </TableBody>
            </Table>
          </TableV2NestedFrame>
        ) : query.data && query.data.items.length > 0 ? (
          <TableV2NestedFrame depth={1}>
            <Table aria-label="Stage runs" sx={tableV2NestedDepthSx[1].table}>
              {buildStageRunColgroup()}
              <TableHead>{header}</TableHead>
              <TableBody>
                {query.data.items.map((stageRun, index, stageRuns) => (
                  <StageRunNestedRow
                    key={stageRun.id}
                    depth={1}
                    isLastVisibleRow={index === stageRuns.length - 1}
                    organisationId={organisationId}
                    projectId={projectId}
                    row={stageRun}
                    showStageLink={false}
                    tagColorByName={tagColorByName}
                  />
                ))}
              </TableBody>
            </Table>
          </TableV2NestedFrame>
        ) : (
          <TableV2NestedEmptyState
            ariaLabel="Stage runs"
            colgroup={buildStageRunColgroup()}
            colSpan={7}
            depth={1}
            header={header}
          />
        )}
      </Box>
    </TableV2NestedCollapse>
  );
};

export const PipelineExpandedRuns = ({
  childCount,
  expanded,
  metricsEndDay,
  metricsStartDay,
  organisationId,
  pipelineId,
  projectId,
  tagColorByName,
}: {
  childCount: number;
  expanded: boolean;
  metricsEndDay: string | null;
  metricsStartDay: string | null;
  organisationId: string | null;
  pipelineId: string;
  projectId: string;
  tagColorByName: TagColorByName;
}): ReactElement => {
  const [sort, setSort] = useState<ApiSort>(defaultPipelineRunSort);
  const handleSortChange = (field: SortablePipelineRunField): void => {
    setSort((current) => ({
      field,
      direction: current.field === field && current.direction === 'desc' ? 'asc' : 'desc',
    }));
  };
  const query = useQuery({
    queryKey: queryKeys.dataGrid(
      gridPaths.builds,
      'v2-nested-pipeline-runs',
      organisationId,
      projectId,
      pipelineId,
      metricsStartDay,
      metricsEndDay,
      sort.field,
      sort.direction,
    ),
    enabled: expanded,
    queryFn: async () =>
      await queryAllTableV2NestedRows(
        {
          page: 1,
          pageSize: 1,
          sorts: [sort],
          filters: buildEqFilter('pipelineId', pipelineId),
          metricsEndDay: metricsEndDay ?? undefined,
          metricsStartDay: metricsStartDay ?? undefined,
        },
        async (nextInput: GridQueryInput): Promise<QueryResult<PipelineRunSummaryItemDto>> => {
          const result = await queryPipelineRunSummary(projectId, organisationId, nextInput);
          return {
            items: result.runSummaries,
            total: result.buildTotal,
            page: nextInput.page,
            pageSize: nextInput.pageSize,
          };
        },
      ),
    staleTime: 30_000,
  });
  const header = <PipelineNestedHeader sort={sort} onSortChange={handleSortChange} />;
  const skeletonRowCount = resolveTableV2NestedSkeletonRowCount(childCount);

  return (
    <TableV2NestedCollapse expanded={expanded}>
      <Box sx={tableV2NestedContentSx}>
        {query.isLoading ? (
          <TableV2NestedFrame depth={1}>
            <Table aria-label="Pipeline runs loading" sx={tableV2NestedDepthSx[1].table}>
              {buildPipelineRunColgroup()}
              <TableHead>{header}</TableHead>
              <TableBody>
                {Array.from({ length: skeletonRowCount }, (_item, index) => (
                  <TableV2SkeletonRow
                    key={`pipeline-runs-nested-skeleton-${String(index)}`}
                    columns={nestedRunSkeletonColumns}
                    isLastVisibleRow={index === skeletonRowCount - 1}
                    rowIndex={index}
                  />
                ))}
              </TableBody>
            </Table>
          </TableV2NestedFrame>
        ) : query.data && query.data.items.length > 0 ? (
          <TableV2NestedFrame depth={1}>
            <Table aria-label="Pipeline runs" sx={tableV2NestedDepthSx[1].table}>
              {buildPipelineRunColgroup()}
              <TableHead>{header}</TableHead>
              <TableBody>
                {query.data.items.map((pipelineRun, index, pipelineRuns) => (
                  <PipelineRunNestedRow
                    key={pipelineRun.id}
                    isLastVisibleRow={index === pipelineRuns.length - 1}
                    organisationId={organisationId}
                    projectId={projectId}
                    row={pipelineRun}
                    tagColorByName={tagColorByName}
                  />
                ))}
              </TableBody>
            </Table>
          </TableV2NestedFrame>
        ) : (
          <TableV2NestedEmptyState
            ariaLabel="Pipeline runs"
            colgroup={buildPipelineRunColgroup()}
            colSpan={7}
            depth={1}
            header={header}
          />
        )}
      </Box>
    </TableV2NestedCollapse>
  );
};

const PipelineRunExpandedStageRuns = ({
  childCount,
  expanded,
  organisationId,
  pipelineExecutionId,
  projectId,
  tagColorByName,
}: {
  childCount: number;
  expanded: boolean;
  organisationId: string | null;
  pipelineExecutionId: string;
  projectId: string;
  tagColorByName: TagColorByName;
}): ReactElement => {
  const [sort, setSort] = useState<ApiSort>(defaultStageRunSort);
  const handleSortChange = (field: SortableStageRunField): void => {
    setSort((current) => ({
      field,
      direction: current.field === field && current.direction === 'desc' ? 'asc' : 'desc',
    }));
  };
  const query = useQuery({
    queryKey: queryKeys.dataGrid(
      'stage-runs',
      'v2-nested-stage-runs',
      organisationId,
      projectId,
      pipelineExecutionId,
      sort.field,
      sort.direction,
    ),
    enabled: expanded,
    queryFn: async () =>
      await queryAllTableV2NestedRows(
        {
          page: 1,
          pageSize: 1,
          sorts: [sort],
          filters: buildEqFilter('pipelineExecutionId', pipelineExecutionId),
        },
        async (
          nextInput: GridQueryInput,
        ): Promise<QueryResult<PipelineStageExecutionListItemDto>> =>
          await queryPipelineStageExecutions(projectId, organisationId, nextInput),
      ),
    staleTime: 30_000,
  });
  const header = <StageRunNestedHeader sort={sort} onSortChange={handleSortChange} />;
  const skeletonRowCount = resolveTableV2NestedSkeletonRowCount(childCount);

  return (
    <TableV2NestedCollapse expanded={expanded}>
      <Box sx={tableV2NestedContentSx}>
        {query.isLoading ? (
          <TableV2NestedFrame depth={2}>
            <Table aria-label="Stage runs loading" sx={tableV2NestedDepthSx[2].table}>
              {buildStageRunColgroup()}
              <TableHead>{header}</TableHead>
              <TableBody>
                {Array.from({ length: skeletonRowCount }, (_item, index) => (
                  <TableV2SkeletonRow
                    key={`stage-runs-nested-skeleton-${String(index)}`}
                    columns={nestedStageRunSkeletonColumns}
                    isLastVisibleRow={index === skeletonRowCount - 1}
                    rowIndex={index}
                  />
                ))}
              </TableBody>
            </Table>
          </TableV2NestedFrame>
        ) : query.data && query.data.items.length > 0 ? (
          <TableV2NestedFrame depth={2}>
            <Table aria-label="Stage runs" sx={tableV2NestedDepthSx[2].table}>
              {buildStageRunColgroup()}
              <TableHead>{header}</TableHead>
              <TableBody>
                {query.data.items.map((stageRun, index, stageRuns) => (
                  <StageRunNestedRow
                    key={stageRun.id}
                    isLastVisibleRow={index === stageRuns.length - 1}
                    organisationId={organisationId}
                    projectId={projectId}
                    row={stageRun}
                    showStageLink
                    tagColorByName={tagColorByName}
                  />
                ))}
              </TableBody>
            </Table>
          </TableV2NestedFrame>
        ) : (
          <TableV2NestedEmptyState
            ariaLabel="Stage runs"
            colgroup={buildStageRunColgroup()}
            colSpan={7}
            depth={2}
            header={header}
          />
        )}
      </Box>
    </TableV2NestedCollapse>
  );
};
